import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Upload, CheckCircle, XCircle, FileText, Camera, Car, User } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { driverService } from '@/services/driverService';

const DriverDocuments: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const driver = currentUser as any;

  const [uploading, setUploading] = useState<string | null>(null);

  const documents = [
    {
      id: 'license',
      title: 'رخصة القيادة',
      description: 'صورة واضحة لرخصة القيادة السارية',
      icon: FileText,
      status: driver?.documentsStatus?.license || 'pending',
      photoURL: driver?.licensePhotoURL,
    },
    {
      id: 'vehicle',
      title: 'صورة السيارة',
      description: 'صورة واضحة للسيارة من الأمام',
      icon: Car,
      status: driver?.documentsStatus?.vehicle || 'pending',
      photoURL: driver?.vehiclePhotoURL,
    },
    {
      id: 'id',
      title: 'البطاقة الشخصية',
      description: 'صورة البطاقة الوطنية أو جواز السفر',
      icon: User,
      status: driver?.documentsStatus?.id || 'pending',
      photoURL: driver?.idPhotoURL,
    },
  ];

  const handleUpload = async (docId: string, file: File) => {
    if (!driver) return;
    
    setUploading(docId);
    try {
      switch (docId) {
        case 'license':
          await driverService.uploadLicensePhoto(driver.id, file);
          break;
        case 'vehicle':
          await driverService.uploadVehiclePhoto(driver.id, file);
          break;
        case 'id':
          await driverService.uploadIdPhoto(driver.id, file);
          break;
      }
      // Reload page to show updated status
      window.location.reload();
    } catch (error) {
      console.error('Error uploading:', error);
    } finally {
      setUploading(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <span className="flex items-center gap-1 text-green-600">
            <CheckCircle className="w-4 h-4" />
            معتمد
          </span>
        );
      case 'rejected':
        return (
          <span className="flex items-center gap-1 text-red-600">
            <XCircle className="w-4 h-4" />
            مرفوض
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 text-yellow-600">
            <ClockIcon className="w-4 h-4" />
            بانتظار المراجعة
          </span>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="flex items-center gap-4 p-4">
          <button
            onClick={() => navigate('/driver/home')}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <ArrowRight className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold">المستندات</h1>
        </div>
      </header>

      <main className="p-6">
        {/* Status Banner */}
        <div className={`rounded-2xl p-4 mb-6 ${
          driver?.isVerified 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-yellow-50 border border-yellow-200'
        }`}>
          <div className="flex items-center gap-3">
            {driver?.isVerified ? (
              <CheckCircle className="w-8 h-8 text-green-600" />
            ) : (
              <ClockIcon className="w-8 h-8 text-yellow-600" />
            )}
            <div>
              <p className={`font-bold ${driver?.isVerified ? 'text-green-800' : 'text-yellow-800'}`}>
                {driver?.isVerified ? 'حساب موثق' : 'بانتظار التحقق'}
              </p>
              <p className={`text-sm ${driver?.isVerified ? 'text-green-700' : 'text-yellow-700'}`}>
                {driver?.isVerified 
                  ? 'يمكنك الآن استقبال الرحلات' 
                  : 'سيتم مراجعة مستنداتك خلال 24 ساعة'}
              </p>
            </div>
          </div>
        </div>

        {/* Documents List */}
        <div className="space-y-4">
          {documents.map((doc) => (
            <div key={doc.id} className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <doc.icon className="w-7 h-7 text-gray-500" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-bold">{doc.title}</h3>
                    {getStatusBadge(doc.status)}
                  </div>
                  <p className="text-gray-500 text-sm mb-4">{doc.description}</p>

                  {/* Preview */}
                  {doc.photoURL && (
                    <div className="mb-4">
                      <img 
                        src={doc.photoURL} 
                        alt={doc.title} 
                        className="w-full h-40 object-cover rounded-xl"
                      />
                    </div>
                  )}

                  {/* Upload Button */}
                  <label className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                    doc.status === 'approved' 
                      ? 'border-green-300 bg-green-50' 
                      : 'border-gray-300 hover:border-secondary-500 hover:bg-secondary-50'
                  }`}>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleUpload(doc.id, file);
                      }}
                      disabled={uploading === doc.id || doc.status === 'approved'}
                    />
                    {uploading === doc.id ? (
                      <>
                        <div className="w-5 h-5 border-2 border-secondary-500 border-t-transparent rounded-full animate-spin" />
                        <span>جاري الرفع...</span>
                      </>
                    ) : doc.status === 'approved' ? (
                      <>
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="text-green-600">تم التحقق</span>
                      </>
                    ) : (
                      <>
                        <Camera className="w-5 h-5" />
                        <span>{doc.photoURL ? 'تحديث الصورة' : 'رفع الصورة'}</span>
                      </>
                    )}
                  </label>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

// Clock icon component
const ClockIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

export default DriverDocuments;
